import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { Store } from '@ngrx/store';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTabsModule } from 'devextreme-angular/ui/tabs';
import { DxTextAreaModule } from 'devextreme-angular/ui/text-area';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { StatusBadgeComponent } from 'src/app/components/library/status-badge/status-badge.component';
import { FormPopupComponent } from 'src/app/components/utils/form-popup/form-popup.component';
import { NotifyService } from 'src/app/core/services/notify.service';
import {
    BLOCK_DESCRIPTION_MAX_LENGTH,
    BLOCK_REASON_TYPES,
    BlockableEntityType,
    BlockReasonType,
    ENTITY_BLOCK_STATUS_TRANSLATION_PREFIX,
    IEntityBlockRecord,
    IEntityBlockUpdateRequest,
} from 'src/app/features/entity-blocks/models/entity-block.models';
import { EntityBlockService } from 'src/app/features/entity-blocks/services/entity-block.service';
import { IRequiredAction, RequiredActionOwnerType } from 'src/app/features/required-actions/models/required-action.models';
import { RequiredActionsService } from 'src/app/features/required-actions/services/required-actions.service';
import { selectUsers } from 'src/app/features/user-management/store/selectors/user-management.selectors';
import { normalizeEntityId } from 'src/app/shared/helpers/entity-id.helper';
import { formatDateWithTransloco } from 'src/app/shared/helpers/format.helper';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { ButtonSection, IFooterButton } from 'src/app/types/footerButtons';

export type EntityManagementDialogMode = 'block' | 'requiredAction';

export interface IEntityManagementFooterContext {
    selectedRequiredAction: IRequiredAction | null;
    selectedBlock: IEntityBlockRecord | null;
    isLoading: boolean;
    isSubmitting: boolean;
    isEditing: boolean;
    canSave: boolean;
    entityId: string | null;
}

export type EntityManagementFooterButtonsFactory = (context: IEntityManagementFooterContext) => IFooterButton[];

interface IEntityBlockEditFormData {
    reason: BlockReasonType | null;
    description: string;
}

@Component({
    selector: 'app-entity-block-management-dialog',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoPipe,
        FormPopupComponent,
        StatusBadgeComponent,
        DxTabsModule,
        DxTemplateModule,
        DxSelectBoxModule,
        DxTextAreaModule,
        DxLoadIndicatorModule,
    ],
    providers: [TranslocoDatePipe],
    templateUrl: './entity-block-management-dialog.component.html',
    styleUrl: './entity-block-management-dialog.component.scss',
})
export class EntityBlockManagementDialogComponent {
    private readonly translocoService = inject(TranslocoService);
    private readonly translocoDatePipe = inject(TranslocoDatePipe);
    private readonly entityBlockService = inject(EntityBlockService);
    private readonly requiredActionsService = inject(RequiredActionsService);
    private readonly store = inject(Store);
    private readonly notifyService = inject(NotifyService);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    readonly descriptionMaxLength = BLOCK_DESCRIPTION_MAX_LENGTH;
    readonly blockedStatus = 'Blocked';
    readonly actionRequiredStatus = 'ActionRequired';

    visible = input(false);
    mode = input<EntityManagementDialogMode>('block');
    entityType = input<BlockableEntityType>(BlockableEntityType.Customer);
    entityId = input<string | null>(null);
    /** When set, parent fully controls footer buttons (e.g. required-action CTAs). */
    footerButtonsFactory = input<EntityManagementFooterButtonsFactory | null>(null);

    visibleChange = output<boolean>();
    entityUpdated = output<unknown>();
    footerAction = output<string>();

    readonly blocks = signal<IEntityBlockRecord[]>([]);
    readonly requiredActions = signal<IRequiredAction[]>([]);
    readonly selectedBlockIndex = signal(0);
    readonly isLoading = signal(false);
    readonly isSubmitting = signal(false);
    readonly isEditing = signal(false);
    readonly users = toSignal(this.store.select(selectUsers), { initialValue: [] });
    readonly editFormData = signal<IEntityBlockEditFormData>({
        reason: null,
        description: '',
    });

    readonly isRequiredActionMode = computed(() => this.mode() === 'requiredAction');

    readonly reasonOptions = computed(() =>
        BLOCK_REASON_TYPES.map(reason => ({
            value: reason,
            text: this.translocoService.translate(`enum_block_reason_${reason}`),
        }))
    );

    readonly entityTypeLabel = computed(() => this.translocoService.translate(`enum_blockable_entity_type_${this.entityType()}`));

    readonly dialogTitleText = computed(() => {
        if (this.isRequiredActionMode()) {
            return this.translocoService.translate('title_entity_action_required', {
                entityType: this.entityTypeLabel(),
            });
        }

        return this.translocoService.translate('title_entity_blocked', {
            entityType: this.entityTypeLabel(),
        });
    });

    readonly dialogTitleIconClass = computed(() => (this.isRequiredActionMode() ? 'cn-icon-ico-alarm' : 'cn-icon-ico-flag-warning'));

    readonly statusTranslationPrefix = computed(() => ENTITY_BLOCK_STATUS_TRANSLATION_PREFIX[this.entityType()]);

    readonly selectedBlock = computed(() => {
        if (this.isRequiredActionMode()) {
            return null;
        }

        const index = this.selectedBlockIndex();
        const items = this.blocks();

        if (index < 0 || index >= items.length) {
            return null;
        }

        return items[index];
    });

    readonly selectedRequiredAction = computed(() => {
        if (!this.isRequiredActionMode()) {
            return null;
        }

        const index = this.selectedBlockIndex();
        const items = this.requiredActions();

        if (index < 0 || index >= items.length) {
            return null;
        }

        return items[index];
    });

    readonly hasSelectedItem = computed(() => (this.isRequiredActionMode() ? !!this.selectedRequiredAction() : !!this.selectedBlock()));

    readonly tabItems = computed(() => {
        if (this.isRequiredActionMode()) {
            return this.requiredActions().map(action => ({
                id: action.id,
                text: this.actionKeyLabel(action.actionKey),
            }));
        }

        return this.blocks().map(block => ({
            id: block.id,
            text: this.translocoService.translate(`enum_block_reason_${block.reason}`),
        }));
    });

    readonly selectedTabItem = computed(() => this.tabItems()[this.selectedBlockIndex()] ?? null);

    readonly selectedReasonLabel = computed(() => {
        if (this.isRequiredActionMode()) {
            return this.actionKeyLabel(this.selectedRequiredAction()?.actionKey);
        }

        const reason = this.isEditing() ? this.editFormData().reason : this.selectedBlock()?.reason;

        if (!reason) {
            return '';
        }

        return this.translocoService.translate(`enum_block_reason_${reason}`);
    });

    readonly selectedReasonDetail = computed(() => {
        if (!this.isRequiredActionMode()) {
            return null;
        }

        return this.actionKeyReason(this.selectedRequiredAction());
    });

    readonly sinceLabel = computed(() => {
        const createdAt = this.isRequiredActionMode() ? this.selectedRequiredAction()?.createdAt : this.selectedBlock()?.createdAt;

        if (!createdAt) {
            return '';
        }

        return formatDateWithTransloco(this.translocoDatePipe, createdAt, false);
    });

    readonly byLabel = computed(() => {
        const createdBy = normalizeEntityId(
            this.isRequiredActionMode() ? this.selectedRequiredAction()?.createdBy : this.selectedBlock()?.createdBy
        );

        if (!createdBy) {
            return this.translocoService.translate('notSet');
        }

        const user = this.users().find(item => normalizeEntityId(item.id) === createdBy);
        return user?.displayName?.trim() || this.translocoService.translate('notSet');
    });

    readonly descriptionValue = computed(() => {
        if (this.isRequiredActionMode()) {
            return this.selectedRequiredAction()?.comment?.trim() || this.selectedReasonDetail() || '';
        }

        return this.selectedBlock()?.description ?? '';
    });

    readonly canSave = computed(() => !!this.editFormData().reason);

    readonly footerContext = computed<IEntityManagementFooterContext>(() => ({
        selectedRequiredAction: this.selectedRequiredAction(),
        selectedBlock: this.selectedBlock(),
        isLoading: this.isLoading(),
        isSubmitting: this.isSubmitting(),
        isEditing: this.isEditing(),
        canSave: this.canSave(),
        entityId: this.entityId(),
    }));

    readonly footerButtons = computed<IFooterButton[]>(() => {
        const factory = this.footerButtonsFactory();
        if (factory) {
            return factory(this.footerContext());
        }

        return this.defaultBlockFooterButtons();
    });

    constructor() {
        effect(() => {
            if (!this.visible()) {
                return;
            }

            this.loadItems();
        });

        effect(() => {
            if (this.isRequiredActionMode()) {
                return;
            }

            const block = this.selectedBlock();

            if (!block || this.isEditing()) {
                return;
            }

            this.resetEditForm(block);
        });
    }

    onVisibleChange(visible: boolean): void {
        this.visibleChange.emit(visible);

        if (!visible) {
            this.resetDialogState();
        }
    }

    onFooterClicked(action: string): void {
        switch (action) {
            case 'update':
                this.startEditing();
                break;
            case 'cancel-edit':
                this.cancelEditing();
                break;
            case 'save':
                this.saveBlock();
                break;
            case 'delete':
                this.confirmDeleteBlock();
                break;
            case 'check-status':
                this.checkRequiredActionStatus();
                break;
            default:
                this.footerAction.emit(action);
                break;
        }
    }

    onTabClick(event: { itemIndex?: number }): void {
        if (this.isEditing() || event.itemIndex == null) {
            return;
        }

        this.selectedBlockIndex.set(event.itemIndex);
    }

    onReasonChanged(reason: BlockReasonType | null): void {
        this.editFormData.update(current => ({
            ...current,
            reason,
        }));
    }

    onDescriptionChanged(description: string): void {
        this.editFormData.update(current => ({
            ...current,
            description,
        }));
    }

    private defaultBlockFooterButtons(): IFooterButton[] {
        if (this.isEditing()) {
            return [
                {
                    text: 'cancel',
                    action: 'cancel-edit',
                    section: ButtonSection.Right,
                    type: 'normal',
                    stylingMode: 'outlined',
                    disabled: this.isSubmitting(),
                },
                {
                    text: 'save',
                    action: 'save',
                    section: ButtonSection.Right,
                    type: 'normal',
                    stylingMode: 'contained',
                    disabled: !this.canSave() || this.isSubmitting(),
                },
            ];
        }

        return [
            {
                text: 'delete_block',
                action: 'delete',
                section: ButtonSection.Right,
                type: 'normal',
                stylingMode: 'outlined',
                disabled: this.isSubmitting() || this.isLoading() || !this.selectedBlock(),
            },
        ];
    }

    private actionKeyLabel(actionKey: string | null | undefined): string {
        if (!actionKey) {
            return this.translocoService.translate('notSet');
        }

        const key = `enum_required_action_key_${actionKey}`;
        const translated = this.translocoService.translate(key);
        return translated === key ? actionKey : translated;
    }

    private actionKeyReason(action: IRequiredAction | null): string {
        if (!action) {
            return '';
        }

        const comment = action.comment?.trim();
        if (comment) {
            return comment;
        }

        if (!action.actionKey) {
            return this.translocoService.translate('notSet');
        }

        const key = `enum_required_action_key_reason_${action.actionKey}`;
        const translated = this.translocoService.translate(key);
        return translated === key ? this.actionKeyLabel(action.actionKey) : translated;
    }

    private loadItems(): void {
        if (this.isRequiredActionMode()) {
            this.loadRequiredActions();
            return;
        }

        this.loadBlocks();
    }

    private loadBlocks(): void {
        const entityId = this.entityId();

        if (!entityId) {
            this.blocks.set([]);
            return;
        }

        this.isLoading.set(true);
        this.entityBlockService
            .getEntityBlocks(this.entityType(), entityId)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    this.blocks.set([]);
                    return EMPTY;
                }),
                tap(blocks => {
                    this.blocks.set(blocks);
                    this.selectedBlockIndex.set(0);
                    this.isEditing.set(false);
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    private loadRequiredActions(): void {
        const entityId = this.entityId();

        if (!entityId) {
            this.requiredActions.set([]);
            return;
        }

        const ownerType = this.mapEntityTypeToOwnerType(this.entityType());
        if (!ownerType) {
            this.requiredActions.set([]);
            return;
        }

        this.isLoading.set(true);
        this.requiredActionsService
            .getOpenRequiredActionsForOwner(ownerType, entityId)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    this.requiredActions.set([]);
                    return EMPTY;
                }),
                tap(actions => {
                    this.requiredActions.set(actions);
                    if (actions.length === 0) {
                        this.onVisibleChange(false);
                        return;
                    }
                    if (this.selectedBlockIndex() >= actions.length) {
                        this.selectedBlockIndex.set(0);
                    }
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    private mapEntityTypeToOwnerType(entityType: BlockableEntityType): RequiredActionOwnerType | null {
        switch (entityType) {
            case BlockableEntityType.Customer:
                return RequiredActionOwnerType.Customer;
            case BlockableEntityType.CustomerOrder:
                return RequiredActionOwnerType.CustomerOrder;
            case BlockableEntityType.ProductionOrder:
                return RequiredActionOwnerType.ProductionOrder;
            default:
                return null;
        }
    }

    private checkRequiredActionStatus(): void {
        const action = this.selectedRequiredAction();
        if (!action || this.isSubmitting()) {
            return;
        }

        this.isSubmitting.set(true);
        this.requiredActionsService
            .checkStatus(action.id)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    return EMPTY;
                }),
                tap(updated => {
                    this.entityUpdated.emit(updated);
                    this.loadRequiredActions();
                }),
                finalize(() => this.isSubmitting.set(false))
            )
            .subscribe();
    }

    private startEditing(): void {
        const block = this.selectedBlock();

        if (!block) {
            return;
        }

        this.resetEditForm(block);
        this.isEditing.set(true);
    }

    private cancelEditing(): void {
        const block = this.selectedBlock();

        if (block) {
            this.resetEditForm(block);
        }

        this.isEditing.set(false);
    }

    private saveBlock(): void {
        const block = this.selectedBlock();
        const entityId = this.entityId();
        const { reason, description } = this.editFormData();

        if (!block || !entityId || !reason || this.isSubmitting()) {
            return;
        }

        const request: IEntityBlockUpdateRequest = {
            id: block.id,
            entityType: this.entityType(),
            entityId,
            reason,
            description: description?.trim() || null,
        };

        this.isSubmitting.set(true);
        this.entityBlockService
            .updateEntityBlock(request)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    return EMPTY;
                }),
                tap(updatedEntity => {
                    this.isEditing.set(false);
                    this.notifyService.notifySuccess(this.translocoService.translate('block_updated_successfully'));
                    this.entityUpdated.emit(updatedEntity);
                    this.loadBlocks();
                }),
                finalize(() => this.isSubmitting.set(false))
            )
            .subscribe();
    }

    private confirmDeleteBlock(): void {
        const block = this.selectedBlock();

        if (!block || this.isSubmitting()) {
            return;
        }

        this.confirmDialogService
            .confirmCloseConfirm(
                this.translocoService.translate('confirm_delete_block_message'),
                this.translocoService.translate('confirm_delete_block_title'),
                { headerIconClass: 'cn-icon-ico-flag-warning' }
            )
            .then(confirmed => {
                if (confirmed) {
                    this.deleteBlock(block.id);
                }
            });
    }

    private deleteBlock(blockId: string): void {
        const entityId = this.entityId();

        if (!entityId || this.isSubmitting()) {
            return;
        }

        this.isSubmitting.set(true);
        this.entityBlockService
            .deleteEntityBlock(blockId, this.entityType(), entityId)
            .pipe(
                catchError(err => {
                    this.notifyService.notifyError(err);
                    return EMPTY;
                }),
                tap(updatedEntity => {
                    this.notifyService.notifySuccess(this.translocoService.translate('block_deleted_successfully'));
                    this.entityUpdated.emit(updatedEntity);
                    this.onVisibleChange(false);
                }),
                finalize(() => this.isSubmitting.set(false))
            )
            .subscribe();
    }

    private resetEditForm(block: IEntityBlockRecord): void {
        this.editFormData.set({
            reason: block.reason,
            description: block.description ?? '',
        });
    }

    private resetDialogState(): void {
        this.blocks.set([]);
        this.requiredActions.set([]);
        this.selectedBlockIndex.set(0);
        this.isEditing.set(false);
        this.isLoading.set(false);
        this.isSubmitting.set(false);
        this.editFormData.set({
            reason: null,
            description: '',
        });
    }
}
